import React, { useState, useEffect, useCallback, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Box,
  Alert,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Tooltip,
  Divider,
  Paper,
} from "@mui/material"
import { Schedule, Delete, Add, Error as ErrorIcon, Warning, CheckCircle, Info, Business } from "@mui/icons-material"
import dayjs from "../utils/dayjsConfig"
import { useQuery } from "@tanstack/react-query"
import CustomBackdrop from "../../../../components/CustomBackdrop"
import { showSuccess } from "../utils/alertUtils"
import { useNavigate } from "react-router-dom"
import {
  obtenerTareasDePaquete,
  obtenerTareaPorCodigo,
  crearTareaParaPanel,
  calcularHorariosParaTareas,
  generarFechasRecurrentes,
  haySuperposicion,
  validarTareaCompleta,
  obtenerPrimerHorarioUsuario,
  formatearHorariosParaTooltip,
} from "../utils/planificacionHelpers"
import CustomAutocomplete from "../../../../components/CustomAutocomplete"
import fetchwrapper from "../../../../services/interceptors/fetchwrapper"
import CustomModalCreateCliente from "../../../../components/CustomModalCreateCliente"
import ModalCreatePlacas from "./ModalCreatePlacas"
import { useQuery as CustomUseQuery } from "../../../../api"

const transformarParaAutocomplete = (datosPlanificacion) => {
  const { paquetes = [], tareas = [] } = datosPlanificacion
  const opcionesTransformadas = []
  paquetes.forEach((paquete) => {
    opcionesTransformadas.push({
      value: `PAQUETE_${paquete.formcodigo}`,
      label: `📦 ${paquete.formdescri} - ${paquete.procesocod || "Sin proceso"}`,
    })
  })
  tareas.forEach((tarea) => {
    opcionesTransformadas.push({
      value: `TAREA_${tarea.pregcodigo}`,
      label: `📝 ${tarea.pregdescri} (${tarea.pregdurmin} min)`,
    })
  })
  return opcionesTransformadas
}

const ModalPlanificacionTareas = ({ open, onClose, selectedDate, onGuardarPlanificacion, slotPreseleccionado }) => {
  const navigate = useNavigate()

  const [modalOpen, setModalOpen] = useState(false)
  const handleOpenModal = () => setModalOpen(true)
  const handleCloseModal = () => setModalOpen(false)

  const [modalOpenPlacas, setModalOpenPlacas] = useState(false)
  const handleOpenModalPlacas = () => setModalOpenPlacas(true)
  const handleCloseModalPlacas = () => setModalOpenPlacas(false)

  const [formData, setFormData] = useState({
    cliente: null,
    clicodigo: "",
    clinombre: "",
    seleccionCombobox: "",
  })

  const [fechaClick, setFechaClick] = useState(null)
  const [horaClick, setHoraClick] = useState(null)

  const [placaSelectedCB, setPlacaSelectedCB] = useState(null)
  const [existenTareasConProcesoTecnicentro, setExistenTareasConProcesoTecnicentro] = useState(false)

  const [tareasEnPanel, setTareasEnPanel] = useState([])
  const [opcionesCombobox, setOpcionesCombobox] = useState([])
  const [opcionSeleccionada, setOpcionSeleccionada] = useState(null)
  const [tareaAdicionalSeleccionadaCombobox, setTareaAdicionalSeleccionadaCombobox] = useState(null)
  const [opcionesTareasAdicionales, setOpcionesTareasAdicionales] = useState([])
  const [datosPlanificacion, setDatosPlanificacion] = useState({ tareas: [], paquetes: [] })

  const [loading, setLoading] = useState(false)
  const [guardandoBackend, setGuardandoBackend] = useState(false)
  const [errores, setErrores] = useState([])
  const [conflictosBackend, setConflictosBackend] = useState([])

  const [showAgregarTarea, setShowAgregarTarea] = useState(false)
  const [tareaAAgregar, setTareaAAgregar] = useState("")

  const [showAgregarTareaReprogramar, setShowAgregarTareaReprogramar] = useState(false)
  const [tareaReprogramarAgregar, setTareaReprogramarAgregar] = useState("")
  const [tareaReprogramarSeleccionadaCombobox, setTareaReprogramarSeleccionadaCombobox] = useState(null)

  const [conflictosPanel, setConflictosPanel] = useState([])
  const [tareasConConflictos, setTareasConConflictos] = useState(new Set())
  const [validacionesPorTarea, setValidacionesPorTarea] = useState({})

  const [usuarioAyuda, setUsuarioAyuda] = useState(null)

  useEffect(() => {
    if (open && slotPreseleccionado) {
      const { usuarioId, usuarioNombre, fecha, hora } = slotPreseleccionado
      if (usuarioId && usuarioNombre) {
        setUsuarioAyuda({
          value: usuarioId,
          label: `${usuarioNombre} (${usuarioId})`,
          usrcodigo: usuarioId,
          usrnombre: usuarioNombre,
        })
      }
      if (fecha) setFechaClick(fecha)
      if (hora) setHoraClick(hora)
    }
  }, [open, slotPreseleccionado])

  const { data: datosSimulados = { tareas: [], paquetes: [] }, isLoading: cargandoDatos } = useQuery({
    queryKey: ["planificacionData"],
    queryFn: async () => {
      const response = await fetchwrapper(`/Agenda/getAllPaquetesYTareas`)
      const result = await response.json()
      return result.data || { tareas: [], paquetes: [] }
    },
    enabled: open,
  })

  const { data: usuariosFake = [], isLoading: cargandoUsuarios } = useQuery({
    queryKey: ["usuariosPlanificacion"],
    queryFn: async () => {
      const response = await fetchwrapper(`/Agenda/getAllUsuariosCB`)
      const result = await response.json()
      return result.data || []
    },
  })

  const {
    data: clientesFake = [],
    isLoading: cargandoClientes,
    refetch: refetchAllClientes,
    isRefetching: isRefetchingAllClientes,
  } = useQuery({
    queryKey: ["clientesPlanificacion"],
    queryFn: async () => {
      const response = await fetchwrapper(`/Agenda/getAllClientes`)
      const result = await response.json()
      return result.data || []
    },
  })

  const { data: horariosUsuariosFake = {}, isLoading: cargandoHorariosUsuarios } = useQuery({
    queryKey: ["horariosUsuarios"],
    queryFn: async () => {
      const response = await fetchwrapper(`/Agenda/getAllHorariosUsuarios`)
      const result = await response.json()
      return result.data || {}
    },
  })

  const { data: dataTareasAReprogramar = [], isLoading: cargandodataTareasAReprogramar } = useQuery({
    queryKey: ["tareasAReprogramar"],
    queryFn: async () => {
      const response = await fetchwrapper(`/Agenda/getTareasAReprogramar`)
      const result = await response.json()
      return result.data || []
    },
  })

  const {
    data: { data: allPlacasCB } = [],
    isLoading: isLoadingAllPlacasCB,
    refetch: refetchAllPlacasCB,
    isRefetching: isRefetchAllPlacasCB,
  } = CustomUseQuery({
    queryKey: ["isLoadingAllPlacasCB"],
    url: "/Agenda/getAllPlacasCB",
    enabled: existenTareasConProcesoTecnicentro,
  })

  useEffect(() => {
    if (open && datosSimulados) {
      setDatosPlanificacion(datosSimulados)
      const opcionesAutocomplete = transformarParaAutocomplete(datosSimulados)
      setOpcionesCombobox(opcionesAutocomplete)
      const soloTareas = datosSimulados.tareas.map((t) => ({
        value: t.pregcodigo,
        label: `📝 ${t.pregdescri} (${t.pregdurmin} min)`,
        ...t,
      }))
      setOpcionesTareasAdicionales(soloTareas)
    }
  }, [open, datosSimulados])

  useEffect(() => {
    if (tareasEnPanel.length > 0) {
      validarTodasLasTareas()
    } else {
      setConflictosPanel([])
      setTareasConConflictos(new Set())
      setValidacionesPorTarea({})
    }
  }, [tareasEnPanel])

  const calcularHoraFin = (horaInicio, duracion) => {
    const [horas, minutos] = horaInicio.split(":").map(Number)
    const minutosTotales = horas * 60 + minutos + duracion
    const horasFin = Math.floor(minutosTotales / 60)
    const minutosFin = minutosTotales % 60
    return `${horasFin.toString().padStart(2, "0")}:${minutosFin.toString().padStart(2, "0")}`
  }

  const resetearEstado = () => {
    setFormData({ cliente: null, clicodigo: "", clinombre: "", seleccionCombobox: "" })
    setTareasEnPanel([])
    setErrores([])
    setConflictosBackend([])
    setConflictosPanel([])
    setTareasConConflictos(new Set())
    setValidacionesPorTarea({})
    setShowAgregarTarea(false)
    setTareaAAgregar("")
    setShowAgregarTareaReprogramar(false)
    setTareaReprogramarAgregar("")
    setTareaReprogramarSeleccionadaCombobox(null)
    setOpcionSeleccionada(null)
    setTareaAdicionalSeleccionadaCombobox(null)
    setUsuarioAyuda(null)
    setFechaClick(null)
    setHoraClick(null)
    setExistenTareasConProcesoTecnicentro(false)
  }

  const validarTodasLasTareas = useCallback(() => {
    const conflictos = []
    const tareasConProblemas = new Set()
    const nuevasValidaciones = {}

    tareasEnPanel.forEach((tarea) => {
      const validacion = validarTareaCompleta(tarea, horariosUsuariosFake, tareasEnPanel)
      nuevasValidaciones[tarea.id] = validacion

      if (validacion.tieneErrores) {
        tareasConProblemas.add(tarea.id)
        conflictos.push({
          tipo: "error_horario_cupo",
          tareaId: tarea.id,
          tareaNombre: tarea.pregdescri,
          usuario: tarea.usuarioAsignado?.usrnombre || "Sin usuario",
          fecha: tarea.fechaInicio || "Sin fecha",
          mensaje: validacion.errores[0] || "Error en validación",
          esBloqueante: true,
        })
      }
    })

    if (tareasEnPanel.length >= 2) {
      const todosLosEventosPanel = []

      tareasEnPanel.forEach((tarea, tareaIndex) => {
        if (!tarea.usuarioAsignado || !tarea.fechaInicio) return
        const repeticiones = Math.max(1, tarea.repeticionesActual || 1)
        const fechaBase = dayjs(tarea.fechaInicio)
        const fechas = generarFechasRecurrentes(fechaBase, tarea.recurrenciaActual || "", repeticiones)

        fechas.forEach((fecha, repeticionIndex) => {
          const inicio = dayjs(`${fecha.format("YYYY-MM-DD")}T${tarea.horaInicio}`)
          const fin = dayjs(`${fecha.format("YYYY-MM-DD")}T${tarea.horaFin}`)

          todosLosEventosPanel.push({
            tareaIndex,
            tareaId: tarea.id,
            tareaNombre: tarea.pregdescri,
            usuarioCodigo: tarea.usuarioAsignado?.usrcodigo,
            usuarioNombre: tarea.usuarioAsignado?.usrnombre,
            repeticion: repeticionIndex + 1,
            fecha,
            inicio,
            fin,
            tieneRecurrencia: tarea.recurrenciaActual && repeticiones > 1,
          })
        })
      })

      const overlapsEncontrados = new Set()

      for (let i = 0; i < todosLosEventosPanel.length; i++) {
        for (let j = i + 1; j < todosLosEventosPanel.length; j++) {
          const eventoA = todosLosEventosPanel[i]
          const eventoB = todosLosEventosPanel[j]

          if (
            eventoA.usuarioCodigo &&
            eventoA.usuarioCodigo === eventoB.usuarioCodigo &&
            eventoA.fecha.isSame(eventoB.fecha, "day")
          ) {
            if (haySuperposicion(eventoA.inicio, eventoA.fin, eventoB.inicio, eventoB.fin)) {
              const key = `${Math.min(i, j)}-${Math.max(i, j)}`
              if (!overlapsEncontrados.has(key)) {
                overlapsEncontrados.add(key)
                tareasConProblemas.add(eventoA.tareaId)
                tareasConProblemas.add(eventoB.tareaId)
                conflictos.push({
                  tipo: "overlap_panel",
                  mensaje: `Usuario "${eventoA.usuarioNombre}" tiene conflicto: "${eventoA.tareaNombre}" se superpone con "${eventoB.tareaNombre}" el ${eventoA.fecha.format("DD/MM/YYYY")}`,
                  esBloqueante: true,
                })
              }
            }
          }
        }
      }
    }

    setConflictosPanel(conflictos)
    setTareasConConflictos(tareasConProblemas)
    setValidacionesPorTarea(nuevasValidaciones)

    return conflictos
  }, [tareasEnPanel, horariosUsuariosFake])

  const validarUI = () => {
    const erroresUI = []

    if (!formData.clicodigo) erroresUI.push("ERROR: Seleccione un cliente")

    tareasEnPanel.forEach((tarea, index) => {
      const numeroTarea = index + 1
      if (!tarea.usuarioAsignado) erroresUI.push(`Tarea ${numeroTarea}: ERROR - No tiene usuario asignado`)
      if (!tarea.fechaInicio) erroresUI.push(`Tarea ${numeroTarea}: ERROR - No tiene fecha de inicio`)
      if (tarea.duracionActual < 1) erroresUI.push(`Tarea ${numeroTarea}: ERROR - Duración debe ser mayor a 0 minutos`)

      const horaInicioRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/
      if (!horaInicioRegex.test(tarea.horaInicio)) {
        erroresUI.push(`Tarea ${numeroTarea}: ERROR - Hora inicio inválida (${tarea.horaInicio})`)
      }
    })

    Object.values(validacionesPorTarea).forEach((validacion) => {
      if (validacion.tieneErrores) erroresUI.push(...validacion.errores)
    })

    conflictosPanel.forEach((conflicto) => erroresUI.push(`CONFLICTO: ${conflicto.mensaje}`))

    return { errores: erroresUI, tieneErrores: erroresUI.length > 0 }
  }

  const getHorarioTarea = (tarea) => {
    const validacionTarea = validacionesPorTarea[tarea.id]
    if (
      validacionTarea &&
      !validacionTarea.tieneErrores &&
      validacionTarea.datosValidacion?.validacionHorario?.horarioEncontrado
    ) {
      const horarioEncontrado = validacionTarea.datosValidacion.validacionHorario.horarioEncontrado
      const fechaTarea = dayjs(tarea.fechaInicio)
      const diaSemana = fechaTarea.day() + 1
      return {
        hrsecuen: horarioEncontrado.secuencia,
        dia: diaSemana,
        horaini: horarioEncontrado.inicio,
        horafin: horarioEncontrado.fin,
        cupo: horarioEncontrado.cupo,
      }
    }
  }

  const manejarGuardar = async () => {
    setLoading(true)
    setErrores([])

    try {
      const { errores: erroresUI, tieneErrores } = validarUI()
      if (tieneErrores) {
        setErrores(erroresUI)
        setLoading(false)
        return
      }

      if (conflictosPanel.length > 0) {
        setErrores(["Hay conflictos de horario o cupo. Corríjalos antes de continuar."])
        setLoading(false)
        return
      }

      setGuardandoBackend(true)
      const datos = {
        cliente: formData.clicodigo,
        clienteNombre: formData.clinombre,
        tareas: tareasEnPanel.map((tarea) => ({
          horario: getHorarioTarea(tarea),
          pregcodigo: tarea.pregcodigo,
          pregdescri: tarea.pregdescri,
          usuario: tarea.usuarioAsignado?.usrcodigo,
          usuarioNombre: tarea.usuarioAsignado?.usrnombre,
          fechaBase: tarea.fechaInicio,
          horaInicio: tarea.horaInicio,
          duracion: tarea.duracionActual,
          recurrencia: tarea.recurrenciaActual || "",
          repeticiones: Math.max(1, tarea.repeticionesActual || 1),
          paqueteCodigo: tarea.paqueteCodigo,
          origen: tarea.origen,
          esEliminable: tarea.esEliminable,
          formsecuen: tarea.formsecuen,
          pregrecuren: tarea.pregrecuren || "",
          pregrecurennum: tarea.pregrecurennum || 0,
          referenciaeventocodigoreprogramado: tarea.eventoOriginalCodigo || null,
          procesocod: tarea?.procesocod || null,
          tecnicoRequerido: !!tarea.tecnicoRequerido,
          ...(tarea?.procesocod === "TECNICENTRO" && {
            placa: placaSelectedCB?.value || null,
          }),
        })),
      }

      const options = {
        method: "POST",
        body: JSON.stringify(datos),
        headers: { "Content-Type": "application/json" },
      }

      // ⭐ REDIRIGIDO AL NUEVO ENDPOINT
      await fetchwrapper("/Agenda/savePlanificacion", options)

      if (onGuardarPlanificacion) navigate(0)

      await showSuccess(`✅ Planificación guardada exitosamente`)
      resetearEstado()
      onClose()
    } catch (error) {
      if (
        error.details &&
        (error.details.errorType === "CUPO_EXCEDIDO" || error.details.errorType === "VALIDACION_FALLIDA")
      ) {
        setConflictosBackend(error.details.validationErrors)
      } else {
        setErrores([error.details?.message || error.message || "Error al guardar planificación"])
      }
    } finally {
      setLoading(false)
      setGuardandoBackend(false)
    }
  }

  const manejarSeleccionPrincipal = (valor) => {
    setFormData((prev) => ({ ...prev, seleccionCombobox: valor }))
    setErrores([])
    setConflictosBackend([])
    setConflictosPanel([])

    if (!valor) return setTareasEnPanel([])

    const [tipo, codigo] = valor.split("_")
    const fechaActual =
      fechaClick || (selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"))

    let horaInicioBase = horaClick || "08:00"
    if (!horaClick && usuarioAyuda) {
      horaInicioBase = obtenerPrimerHorarioUsuario(usuarioAyuda.usrcodigo, fechaActual, horariosUsuariosFake)
    }

    if (tipo === "PAQUETE") {
      const tareasDelPaquete = obtenerTareasDePaquete(datosPlanificacion, codigo)
      setExistenTareasConProcesoTecnicentro(tareasDelPaquete?.[0]?.procesocod === "TECNICENTRO")

      const tareasParaPanel = tareasDelPaquete.map((tareaDatos) =>
        crearTareaParaPanel(tareaDatos, "automatico", codigo, tareaDatos.formsecuen),
      )

      const tareasConDatosCompletos = tareasParaPanel.map((tarea) => ({
        ...tarea,
        usuarioAsignado: usuarioAyuda,
        fechaInicio: fechaActual,
        tecnicoRequerido: false,
      }))

      const tareasConHorarios = calcularHorariosParaTareas(tareasConDatosCompletos, horaInicioBase)
      setTareasEnPanel(tareasConHorarios)
    } else if (tipo === "TAREA") {
      const tareaDatos = obtenerTareaPorCodigo(datosPlanificacion, codigo)
      if (tareaDatos) {
        const tareaParaPanel = crearTareaParaPanel(tareaDatos, "automatico", null, null)
        const tareaConDatosCompletos = {
          ...tareaParaPanel,
          usuarioAsignado: usuarioAyuda,
          fechaInicio: fechaActual,
          tecnicoRequerido: false,
        }
        const tareaConHorario = calcularHorariosParaTareas([tareaConDatosCompletos], horaInicioBase)[0]
        setTareasEnPanel([tareaConHorario])
      }
    }
  }

  const manejarAgregarTarea = () => {
    if (!tareaAAgregar) return
    const tareaDatos = obtenerTareaPorCodigo(datosPlanificacion, tareaAAgregar)
    if (!tareaDatos) return

    const nuevaTarea = crearTareaParaPanel(tareaDatos, "manual", null, null)
    const fechaActual =
      fechaClick || (selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"))

    let horaInicioBase = horaClick || "08:00"
    if (!horaClick && usuarioAyuda) {
      horaInicioBase = obtenerPrimerHorarioUsuario(usuarioAyuda.usrcodigo, fechaActual, horariosUsuariosFake)
    }

    const nuevaTareaConDatos = {
      ...nuevaTarea,
      usuarioAsignado: usuarioAyuda,
      fechaInicio: fechaActual,
      tecnicoRequerido: false,
    }
    const ultimaTarea = tareasEnPanel[tareasEnPanel.length - 1]
    const horaInicio = ultimaTarea ? ultimaTarea.horaFin : horaInicioBase
    const nuevaTareaConHorario = calcularHorariosParaTareas([nuevaTareaConDatos], horaInicio)[0]

    setTareasEnPanel((prev) => [...prev, nuevaTareaConHorario])
    setTareaAAgregar("")
    setTareaAdicionalSeleccionadaCombobox(null)
    setShowAgregarTarea(false)
  }

  const manejarAgregarTareaReprogramar = () => {
    if (!tareaReprogramarSeleccionadaCombobox) return
    const eventoReprogramado = tareaReprogramarSeleccionadaCombobox

    const nuevaTareaReprogramada = {
      id: `reprogramacion-${eventoReprogramado.eventocodigo}-${Date.now()}`,
      pregcodigo: eventoReprogramado.pregcodigo,
      pregdescri: eventoReprogramado.pregdescri,
      pregdurmin: eventoReprogramado.eventoduracion || eventoReprogramado.pregdurmin || 30,
      usuarioAsignado: {
        value: eventoReprogramado.usrcodigo,
        label: `${eventoReprogramado.usrcodigo} (${eventoReprogramado.usrnombre})`,
        usrcodigo: eventoReprogramado.usrcodigo,
        usrnombre: eventoReprogramado.usrnombre,
      },
      fechaInicio: fechaClick || dayjs().format("YYYY-MM-DD"),
      horaInicio: horaClick || "08:00",
      duracionActual: eventoReprogramado.eventoduracion || eventoReprogramado.pregdurmin || 30,
      horaFin: calcularHoraFin(
        horaClick || "08:00",
        eventoReprogramado.eventoduracion || eventoReprogramado.pregdurmin || 30,
      ),
      recurrenciaActual: "",
      repeticionesActual: 0,
      esReprogramacion: true,
      eventoOriginalCodigo: eventoReprogramado.eventocodigo,
      origen: "reprogramacion",
      tecnicoRequerido: false,
      clienteData: { clicodigo: eventoReprogramado.clicodigo, clinombre: eventoReprogramado.clinombre },
      paqueteCodigo: eventoReprogramado.paquetecodigo,
      formsecuen: eventoReprogramado.formsecuen,
      esEliminable: true,
    }

    if (!formData.clicodigo && eventoReprogramado.clicodigo) {
      setFormData((prev) => ({
        ...prev,
        cliente: {
          value: eventoReprogramado.clicodigo,
          label: `${eventoReprogramado.clinombre} (${eventoReprogramado.clicodigo})`,
          clicodigo: eventoReprogramado.clicodigo,
          clinombre: eventoReprogramado.clinombre,
        },
        clicodigo: eventoReprogramado.clicodigo,
        clinombre: eventoReprogramado.clinombre,
      }))
    }

    setTareasEnPanel((prev) => [...prev, nuevaTareaReprogramada])
    setTareaReprogramarSeleccionadaCombobox(null)
    setShowAgregarTareaReprogramar(false)
  }

  const manejarModificarTarea = (tareaId, campo, valor) => {
    setTareasEnPanel((prev) =>
      prev.map((tarea) => {
        if (tarea.id === tareaId) {
          const actualizada = { ...tarea, [campo]: valor }
          if (campo === "horaInicio" || campo === "duracionActual") {
            actualizada.horaFin = calcularHoraFin(actualizada.horaInicio, actualizada.duracionActual)
          }
          if (tarea.esReprogramacion) {
            actualizada.recurrenciaActual = ""
            actualizada.repeticionesActual = 0
          } else if (campo === "recurrenciaActual" && (!valor || valor === "")) {
            actualizada.repeticionesActual = 0
          }
          return actualizada
        }
        return tarea
      }),
    )
  }

  const manejarEliminarTarea = (tareaId) => {
    setTareasEnPanel((prev) => prev.filter((t) => t.id !== tareaId))
  }

  const renderTarea = (tarea, index) => {
    const esDelPaquete = tarea.paqueteCodigo !== null
    const esEliminable = tarea.esEliminable
    const tieneRecurrencia = tarea.recurrenciaActual && tarea.repeticionesActual > 1
    const tieneConflicto = tareasConConflictos.has(tarea.id)
    const validacionTarea = validacionesPorTarea[tarea.id]
    const tieneUsuarioYFecha = tarea.usuarioAsignado && tarea.fechaInicio
    const esReprogramacion = tarea.esReprogramacion

    let estadoValidacion = "pendiente"
    let mensajeValidacion = ""
    let infoCupo = ""

    if (validacionTarea) {
      if (validacionTarea.tieneErrores) {
        estadoValidacion = "error"
        mensajeValidacion = validacionTarea.errores[0]
      } else if (validacionTarea.datosValidacion) {
        estadoValidacion = "valido"
        const { validacionHorario, validacionCupo } = validacionTarea.datosValidacion
        if (validacionHorario.horarioEncontrado) mensajeValidacion = validacionHorario.mensaje
        infoCupo = validacionCupo.esIlimitado
          ? "∞ (Ilimitado)"
          : `${validacionCupo.cupoActual}/${validacionCupo.cupoMaximo}`
      }
    }

    let tooltipHorarios = null
    if (tieneUsuarioYFecha) {
      tooltipHorarios = formatearHorariosParaTooltip(
        tarea.usuarioAsignado.usrcodigo,
        tarea.fechaInicio,
        horariosUsuariosFake,
      )
    }

    return (
      <Paper
        key={tarea.id}
        elevation={2}
        sx={{
          p: 2,
          mb: 2,
          mt: 2.5, // ⭐ MARGEN PARA EVITAR CORTES CON EL SCROLL
          borderRadius: 2,
          borderLeft: tarea.tecnicoRequerido
            ? "6px solid #d32f2f"
            : estadoValidacion === "error"
              ? "6px solid #f44336"
              : "6px solid #2e7d32",
          bgcolor: estadoValidacion === "error" ? "#ffebee" : "#fff",
          position: "relative",
          overflow: "visible",
        }}
      >
        <Box sx={{ position: "absolute", top: -12, right: 12, display: "flex", gap: 1 }}>
          {estadoValidacion === "error" && <Chip icon={<ErrorIcon />} label="Error" color="error" size="small" />}
          {estadoValidacion === "valido" && (
            <Chip icon={<CheckCircle />} label="Validado" color="success" size="small" />
          )}
          {infoCupo && estadoValidacion === "valido" && <Chip label={`📊 ${infoCupo}`} size="small" color="info" />}

          {tieneUsuarioYFecha && (
            <Tooltip
              title={
                <Typography variant="caption" sx={{ whiteSpace: "pre-line" }}>
                  {tooltipHorarios?.contenido}
                </Typography>
              }
              arrow
            >
              <Chip icon={<Info />} label="Horarios" size="small" variant="outlined" sx={{ bgcolor: "white" }} />
            </Tooltip>
          )}
        </Box>

        <Grid container spacing={2} alignItems="center" mt={1}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2" fontWeight="bold" color="primary">
              {esDelPaquete ? "📦" : esReprogramacion ? "🔄" : "📝"} {tarea.pregcodigo}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
              {tarea.pregdescri}
            </Typography>
            {tarea.paqueteCodigo && (
              <Chip
                label={`Paquete: ${tarea.paqueteCodigo}`}
                size="small"
                color="info"
                variant="outlined"
                sx={{ mt: 1, height: 18, fontSize: "0.65rem" }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={8}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <CustomAutocomplete
                  label="Usuario asignado *"
                  selectedOption={tarea.usuarioAsignado}
                  setSelectedOption={(usuario) => manejarModificarTarea(tarea.id, "usuarioAsignado", usuario)}
                  options={usuariosFake}
                  error={!tarea.usuarioAsignado}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  type="date"
                  label="Fecha inicio *"
                  value={tarea.fechaInicio || ""}
                  onChange={(e) => manejarModificarTarea(tarea.id, "fechaInicio", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  error={!tarea.fechaInicio}
                />
              </Grid>
              <Grid item xs={4} md={3}>
                <TextField
                  label="H. Inicio *"
                  value={tarea.horaInicio}
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  error={tieneConflicto || estadoValidacion === "error"}
                  onChange={(e) => manejarModificarTarea(tarea.id, "horaInicio", e.target.value)}
                />
              </Grid>
              <Grid item xs={4} md={3}>
                <TextField
                  label="Duración *"
                  value={tarea.duracionActual}
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  error={tieneConflicto || estadoValidacion === "error"}
                  onChange={(e) => manejarModificarTarea(tarea.id, "duracionActual", parseInt(e.target.value) || 1)}
                />
              </Grid>
              <Grid item xs={4} md={3}>
                <TextField
                  label="H. Fin"
                  value={tarea.horaFin}
                  type="time"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  size="small"
                  disabled
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}
              >
                <Tooltip
                  title={
                    tarea.tecnicoRequerido
                      ? "Técnico Requerido Fijo (Bloqueado en Agenda)"
                      : "Permite Reasignación en Agenda"
                  }
                >
                  <Button
                    size="small"
                    variant={tarea.tecnicoRequerido ? "contained" : "outlined"}
                    color={tarea.tecnicoRequerido ? "error" : "success"}
                    onClick={() => manejarModificarTarea(tarea.id, "tecnicoRequerido", !tarea.tecnicoRequerido)}
                    sx={{ minWidth: 35, p: 0.5 }}
                  >
                    {tarea.tecnicoRequerido ? "Fijo" : "Móvil"}
                  </Button>
                </Tooltip>
                {esEliminable && (
                  <IconButton color="error" onClick={() => manejarEliminarTarea(tarea.id)} size="small">
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            {!esReprogramacion && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  bgcolor: "#fcfcfc",
                  p: 1,
                  mt: 1,
                  borderRadius: 1,
                  border: "1px dashed #e0e0e0",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Recurrencia</InputLabel>
                  <Select
                    value={tarea.recurrenciaActual || ""}
                    label="Recurrencia"
                    onChange={(e) => manejarModificarTarea(tarea.id, "recurrenciaActual", e.target.value)}
                  >
                    <MenuItem value="">Sin Repetición</MenuItem>
                    <MenuItem value="diaria">Diaria</MenuItem>
                    <MenuItem value="semanal">Semanal</MenuItem>
                    <MenuItem value="mensual">Mensual</MenuItem>
                    <MenuItem value="anual">Anual</MenuItem>
                  </Select>
                </FormControl>

                {tarea.recurrenciaActual && tarea.recurrenciaActual !== "" && (
                  <TextField
                    type="number"
                    label="Repeticiones"
                    size="small"
                    value={tarea.repeticionesActual}
                    onChange={(e) =>
                      manejarModificarTarea(tarea.id, "repeticionesActual", Math.max(1, parseInt(e.target.value) || 1))
                    }
                    sx={{ width: 150 }}
                  />
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>
    )
  }

  const hayErroresBloqueantes = useMemo(() => {
    return conflictosPanel.length > 0 || Object.values(validacionesPorTarea).some((v) => v.tieneErrores)
  }, [conflictosPanel, validacionesPorTarea])

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          resetearEstado()
          onClose()
        }
      }}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          width: "90vw",
          maxWidth: "1200px",
          height: "95vh",
          maxHeight: "900px",
          margin: 2,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <CustomBackdrop
        isLoading={
          cargandoDatos ||
          cargandoUsuarios ||
          cargandoClientes ||
          isRefetchingAllClientes ||
          loading ||
          cargandoHorariosUsuarios ||
          guardandoBackend ||
          cargandodataTareasAReprogramar ||
          isLoadingAllPlacasCB ||
          isRefetchAllPlacasCB
        }
      />

      <DialogTitle sx={{ bgcolor: "primary.main", color: "white", py: 2, flexShrink: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Schedule sx={{ mr: 2 }} />
            <Typography variant="h5">Planificar Tareas</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {hayErroresBloqueantes && (
              <Chip
                icon={<ErrorIcon />}
                label={`${conflictosPanel.length + Object.values(validacionesPorTarea).filter((v) => v.tieneErrores).length} errores`}
                color="error"
                size="small"
              />
            )}
            <Chip
              label={`${tareasEnPanel.length} tareas`}
              variant="outlined"
              sx={{ color: "white", borderColor: "white" }}
            />
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, bgcolor: "#fdfdfd" }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          {errores.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrores([])}>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {errores.slice(0, 3).map((err, i) => (
                  <li key={i}>
                    <Typography variant="body2">{err}</Typography>
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          {conflictosPanel.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                ⚠️ Conflictos detectados ({conflictosPanel.length}):
              </Typography>
              {conflictosPanel.slice(0, 2).map((c, i) => (
                <Typography key={i} variant="body2">
                  • {c.mensaje}
                </Typography>
              ))}
            </Alert>
          )}
          {conflictosBackend.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConflictosBackend([])}>
              <Typography variant="subtitle2" gutterBottom>
                Errores de validación backend:
              </Typography>
              {conflictosBackend.slice(0, 2).map((c, i) => (
                <Typography key={i} variant="body2">
                  • {c.pregdescri}: {c.message}
                </Typography>
              ))}
            </Alert>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: "hidden", display: "flex" }}>
          <Grid container spacing={3} sx={{ height: "100%", flex: 1, overflow: "hidden" }}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderTop: "4px solid #196C87",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="primary"
                  sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Business fontSize="small" /> Datos de Configuración Inicial
                </Typography>

                <Grid container rowSpacing={2} columnSpacing={2}>
                  <Grid item xs={12}>
                    <CustomAutocomplete
                      label="Cliente *"
                      selectedOption={formData.cliente}
                      setSelectedOption={(v) =>
                        setFormData((prev) => ({
                          ...prev,
                          cliente: v,
                          clicodigo: v?.clicodigo ?? null,
                          clinombre: v?.clinombre ?? null,
                        }))
                      }
                      options={clientesFake}
                      error={!formData.clicodigo}
                    />
                  </Grid>

                  {existenTareasConProcesoTecnicentro && (
                    <Grid item xs={12}>
                      <CustomAutocomplete
                        label="Placas *"
                        selectedOption={placaSelectedCB}
                        setSelectedOption={setPlacaSelectedCB}
                        options={allPlacasCB}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <CustomAutocomplete
                      label="Usuario Predeterminado"
                      selectedOption={usuarioAyuda}
                      setSelectedOption={setUsuarioAyuda}
                      options={usuariosFake}
                      disabled={!formData.clicodigo || cargandoUsuarios}
                      placeholder="Este usuario se autocompletará en las tareas"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <CustomAutocomplete
                      label="Inyección al Panel *"
                      selectedOption={opcionSeleccionada}
                      setSelectedOption={(nuevaOpcion) => {
                        setOpcionSeleccionada(nuevaOpcion)
                        manejarSeleccionPrincipal(nuevaOpcion ? nuevaOpcion.value : "")
                      }}
                      options={opcionesCombobox || []}
                      disabled={cargandoDatos || loading || !formData.clicodigo}
                      placeholder="Buscar paquete o tarea..."
                    />
                  </Grid>

                  {existenTareasConProcesoTecnicentro && (
                    <Grid item xs={12} sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button onClick={handleOpenModal} variant="contained" size="small" sx={{ flex: 1 }}>
                        Nuevo Cliente
                      </Button>
                      <CustomModalCreateCliente
                        open={modalOpen}
                        onClose={handleCloseModal}
                        onSuccess={() => refetchAllClientes()}
                      />
                      <Button onClick={handleOpenModalPlacas} variant="contained" size="small" sx={{ flex: 1 }}>
                        Nueva Placa
                      </Button>
                      <ModalCreatePlacas
                        open={modalOpenPlacas}
                        onClose={handleCloseModalPlacas}
                        onSuccess={() => refetchAllPlacasCB()}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    {!showAgregarTarea ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setShowAgregarTarea(true)}
                        disabled={loading || !formData.clicodigo}
                      >
                        Agregar tarea manual al panel
                      </Button>
                    ) : (
                      <Box>
                        <CustomAutocomplete
                          label="Seleccionar tarea suelta"
                          selectedOption={tareaAdicionalSeleccionadaCombobox}
                          setSelectedOption={(v) => {
                            setTareaAdicionalSeleccionadaCombobox(v)
                            setTareaAAgregar(v ? v.value : "")
                          }}
                          options={opcionesTareasAdicionales}
                        />
                        <Box display="flex" gap={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={manejarAgregarTarea}
                            disabled={!tareaAAgregar || loading}
                            sx={{ flex: 1 }}
                          >
                            Agregar
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setShowAgregarTarea(false)
                              setTareaAAgregar("")
                              setTareaAdicionalSeleccionadaCombobox(null)
                            }}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    {!showAgregarTareaReprogramar ? (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setShowAgregarTareaReprogramar(true)}
                        disabled={loading || cargandodataTareasAReprogramar}
                        sx={{ color: "warning.dark", borderColor: "warning.light", bgcolor: "#fff8e1" }}
                      >
                        Rescatar tarea reprogramada
                      </Button>
                    ) : (
                      <Box sx={{ p: 1, bgcolor: "#fff8e1", borderRadius: 1, border: "1px dashed orange" }}>
                        <CustomAutocomplete
                          label="Seleccionar evento a rescatar"
                          selectedOption={tareaReprogramarSeleccionadaCombobox}
                          setSelectedOption={setTareaReprogramarSeleccionadaCombobox}
                          options={dataTareasAReprogramar.map((e) => ({
                            value: e.eventocodigo,
                            label: `🔄 ${e.pregdescri} (${dayjs(e.eventofecha).format("DD/MM/YYYY")})`,
                            ...e,
                          }))}
                        />
                        <Box display="flex" gap={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={manejarAgregarTareaReprogramar}
                            disabled={!tareaReprogramarSeleccionadaCombobox || loading}
                            sx={{ flex: 1, bgcolor: "warning.main" }}
                          >
                            Importar
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setShowAgregarTareaReprogramar(false)
                              setTareaReprogramarSeleccionadaCombobox(null)
                            }}
                          >
                            Cancelar
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid
              item
              xs={12}
              md={8}
              sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              <Box sx={{ mb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                  Cola de Ejecución
                </Typography>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pr: 2, pt: 1, pb: 2 }}>
                {tareasEnPanel.length > 0 ? (
                  tareasEnPanel.map(renderTarea)
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 6,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      borderStyle: "dashed",
                      borderRadius: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Schedule sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      No hay tareas en el panel
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Seleccione un cliente y luego un paquete o tarea del menú izquierdo.
                    </Typography>
                  </Paper>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ p: 2.5, bgcolor: "#f8f9fa", borderTop: "1px solid #e0e0e0", justifyContent: "space-between" }}
      >
        <Typography variant="body2" color="textSecondary">
          Total eventos a crear:{" "}
          <b>{tareasEnPanel.reduce((sum, t) => sum + Math.max(1, t.repeticionesActual || 1), 0)}</b>
        </Typography>
        <Box>
          <Button
            onClick={() => {
              resetearEstado()
              onClose()
            }}
            disabled={loading}
            variant="outlined"
            color="inherit"
            sx={{ px: 4, mr: 2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={manejarGuardar}
            variant="contained"
            disabled={loading || tareasEnPanel.length === 0 || hayErroresBloqueantes}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{ minWidth: 220, bgcolor: "#196C87", "&:hover": { bgcolor: "#114B5E" } }}
          >
            {loading ? "Procesando..." : "Confirmar Planificación"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}

export default ModalPlanificacionTareas
